import React, { useState } from "react";
import { Check, ChevronsUpDown, Loader2, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import useDebounce from "@/hooks/useDebounce";
import { cn } from "@/lib/utils";
import Consultant from "@/models/Consultant";
import { useConsultants } from "@/networking/consultant.hooks";

export const consultantLabel = (consultant: Consultant) =>
	`${consultant.firstName ?? ""} ${consultant.lastName ?? ""}`.trim() ||
	consultant.email ||
	consultant.id;

interface Props {
	value?: Consultant;
	onChange: (consultant: Consultant) => void;
	placeholder?: string;
}

/**
 * Searchable consultant select — a popover with a keyword field backed by the
 * server-side consultant search (`GET /consultant-account`), so it works for
 * organisations with thousands of consultants. Used by the person-file
 * template editor to pick who the preview is rendered for.
 */
const ConsultantPicker: React.FC<Props> = ({ value, onChange, placeholder = "Välj konsult…" }) => {
	const [open, setOpen] = useState(false);
	const [search, setSearch] = useState("");
	const debouncedSearch = useDebounce(search, 300);

	const { consultantList, isFetching } = useConsultants(
		{ page: 1, pageSize: 20, sort: "name", keyword: debouncedSearch || undefined },
		open
	);

	const consultants = consultantList?.users ?? [];

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<button
					type="button"
					className="flex h-9 w-full items-center justify-between gap-2 rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-700 hover:bg-gray-50"
				>
					<span className={cn("truncate", !value && "text-gray-400")}>
						{value ? consultantLabel(value) : placeholder}
					</span>
					<ChevronsUpDown size={14} className="shrink-0 text-gray-400" />
				</button>
			</PopoverTrigger>
			<PopoverContent align="start" className="w-[22rem] p-0">
				<div className="flex items-center gap-2 border-b border-gray-200 px-3 py-2">
					<Search size={14} className="shrink-0 text-gray-400" />
					<Input
						autoFocus
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						placeholder="Sök konsult"
						className="h-7 border-0 px-0 shadow-none focus-visible:ring-0"
					/>
					{isFetching && <Loader2 size={14} className="shrink-0 animate-spin text-gray-400" />}
				</div>
				<div className="max-h-72 overflow-y-auto py-1">
					{!isFetching && !consultants.length && (
						<div className="px-3 py-2 text-xs text-gray-400">Inga konsulter hittades</div>
					)}
					{consultants.map((consultant) => (
						<button
							key={consultant.id}
							type="button"
							className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-50"
							onClick={() => {
								onChange(consultant);
								setOpen(false);
							}}
						>
							<Check
								size={14}
								className={cn(
									"shrink-0 text-brand",
									consultant.id === value?.id ? "opacity-100" : "opacity-0"
								)}
							/>
							<span className="truncate">{consultantLabel(consultant)}</span>
							{consultant.profile?.employeeNumber != null && (
								<span className="ml-auto shrink-0 font-mono text-[11px] text-gray-400">
									#{consultant.profile.employeeNumber}
								</span>
							)}
						</button>
					))}
				</div>
			</PopoverContent>
		</Popover>
	);
};

export default ConsultantPicker;
